import { Modal } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui';
import { AlertTriangle, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CONFIGURACION_SESION } from '@/config/configuracion';
import styles from './ModalAdvertenciaSesion.module.css';

interface ModalAdvertenciaSesionProps {
    /** Si el modal está abierto */
    open: boolean;
    /** Minutos restantes hasta expiración */
    minutesRemaining: number;
    /** Callback al cerrar el modal (continuar sin renovar) */
    onClose: () => void;
    /** Callback para renovar sesión (1 hora más) */
    onRenewSession: () => void;
    /** Callback para cerrar sesión inmediatamente */
    onLogoutNow: () => void;
    /** Si está renovando token */
    isRefreshing: boolean;
}

/**
 * ModalAdvertenciaSesion - Modal de advertencia de sesión próxima a expirar
 * 
 * Aparece 2 minutos antes de que expire el token
 * Countdown 2 minutos: Si no interactúa, cierra sesión automáticamente cuando llegue a 0
 * 
 * Opciones:
 * - Seguir Conectado: Renueva el token por 1 hora más
 * - Cerrar Sesión: Logout inmediato
 * - Continuar: Modal se cierra, sesión expirará automáticamente en 2 min
 */
export const ModalAdvertenciaSesion = ({
    open,
    minutesRemaining,
    onClose,
    onRenewSession,
    onLogoutNow,
    isRefreshing,
}: ModalAdvertenciaSesionProps) => {
    const COUNTDOWN_INICIAL = CONFIGURACION_SESION.DURACION_COUNTDOWN_SESSION;
    const [countdown, setCountdown] = useState<number>(COUNTDOWN_INICIAL);

    // Countdown desde configuración (2 minutos = 120 segundos)
    useEffect(() => {
        if (!open) {
            setCountdown(COUNTDOWN_INICIAL); // Reset cuando se cierra
            return;
        }

        // Si está renovando, pausar countdown
        if (isRefreshing) {
            return;
        }

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    // Auto-logout cuando llega a 0 (coincide con expiración token)
                    onLogoutNow();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [open, onLogoutNow, isRefreshing]);

    const handleAction = (action: () => void) => {
        // Detener countdown al interactuar
        setCountdown(COUNTDOWN_INICIAL);
        action();
    };

    // Formatear countdown como MM:SS
    const formatCountdown = (segundos: number) => {
        const mins = Math.floor(segundos / 60);
        const secs = segundos % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Modal isOpen={open} onClose={() => handleAction(onClose)} size="md" closeOnOverlayClick={false}>
            <div className={styles.modalContent}>
                {/* Icon */}
                <div className={styles.iconWrapper}>
                    <AlertTriangle className={styles.warningIcon} size={48} />
                </div>

                {/* Title */}
                <h2 className={styles.title}>Sesión Por Expirar</h2>

                {/* Time Info */}
                <div className={styles.timeInfo}>
                    <Clock size={20} />
                    <span className={styles.timeText}>
                        Tu sesión expirará en <strong>{minutesRemaining} minuto{minutesRemaining !== 1 ? 's' : ''}</strong>
                    </span>
                </div>

                {/* Description */}
                <p className={styles.description}>
                    ¿Deseas seguir conectado? Si eliges seguir conectado, tu sesión se renovará por 1 hora más.
                </p>

                {/* Countdown Warning */}
                <div className={styles.countdownWarning}>
                    <span className={styles.countdownText}>
                        Se cerrará sesión automáticamente en <strong className={styles.countdownNumber}>{formatCountdown(countdown)}</strong>
                    </span>
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <Button
                        variant="secondary"
                        outlined
                        onClick={() => handleAction(onClose)}
                        className={styles.continueButton}
                        disabled={isRefreshing}
                    >
                        Continuar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleAction(onLogoutNow)}
                        className={styles.logoutButton}
                        disabled={isRefreshing}
                    >
                        Cerrar Sesión
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => handleAction(onRenewSession)}
                        className={styles.renewButton}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? 'Renovando...' : 'Seguir Conectado'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
